// ===========================================
// Admin Settings Page
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useState } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import {
  Palette as ColorIcon,
  CalendarMonth as CalendarIcon,
  Menu as NavigationIcon,
  Domain as DomainIcon,
  AccountCircle as AvatarIcon,
  Gavel as LegalIcon,
  Event as EventIcon,
  HowToReg as OnboardingIcon,
  Security as SecurityIcon,
  ContactPhone as EnquiriesIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import ColorsTab from '../../components/admin/settings/ColorsTab';
import AcademicYearTab from '../../components/admin/settings/AcademicYearTab';
import NavigationSettingsTab from '../../components/admin/settings/NavigationSettingsTab';
import SchoolInfoTab from '../../components/admin/settings/SchoolInfoTab';
import AvatarSettingsTab from '../../components/admin/settings/AvatarSettingsTab';
import LegalSettingsTab from '../../components/admin/settings/LegalSettingsTab';
import ExtraLessonSettingsTab from '../../components/admin/settings/ExtraLessonSettingsTab';
import OnboardingSettingsTab from '../../components/admin/settings/OnboardingSettingsTab';
import SecurityTab from '../../components/admin/settings/SecurityTab';
import EnquirySettingsTab from '../../components/admin/settings/EnquirySettingsTab';
import { usePermissions } from '../../hooks/usePermissions';

// ===========================================
// TYPES
// ===========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ===========================================
// TAB PANEL COMPONENT
// ===========================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState(0);
  // Security holds "Force logout all", which the server keeps to the owner tier (adminOnly).
  // It is the LAST tab, so hiding it leaves every other tab's index unchanged (roles 1b §5.15).
  // Enquiries (8) sits before it and Security is 9; both carry an explicit `value`, so a hidden
  // Enquiries tab can never shift Security's index.
  const { isOwner, can } = usePermissions();
  const showEnquiries = can('system_settings', 'read');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <PageHeader
        title="System Settings"
        subtitle="Configure school-wide settings for colors, navigation, and more"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'System Settings' },
        ]}
      />

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<DomainIcon />}
            iconPosition="start"
            label="School Info"
            {...a11yProps(0)}
          />
          <Tab
            icon={<ColorIcon />}
            iconPosition="start"
            label="Colors"
            {...a11yProps(1)}
          />
          <Tab
            icon={<CalendarIcon />}
            iconPosition="start"
            label="Academic Year"
            {...a11yProps(2)}
          />
          <Tab
            icon={<AvatarIcon />}
            iconPosition="start"
            label="Avatar Settings"
            {...a11yProps(3)}
          />
          <Tab
            icon={<NavigationIcon />}
            iconPosition="start"
            label="Navigation"
            {...a11yProps(4)}
          />
          <Tab
            icon={<EventIcon />}
            iconPosition="start"
            label="Extra Lessons"
            {...a11yProps(5)}
          />
          <Tab
            icon={<LegalIcon />}
            iconPosition="start"
            label="Legal"
            {...a11yProps(6)}
          />
          <Tab
            icon={<OnboardingIcon />}
            iconPosition="start"
            label="Student Onboarding"
            {...a11yProps(7)}
          />
          {showEnquiries && (
            <Tab
              icon={<EnquiriesIcon />}
              iconPosition="start"
              label="Enquiries"
              value={8}
              {...a11yProps(8)}
            />
          )}
          {isOwner && (
            <Tab
              icon={<SecurityIcon />}
              iconPosition="start"
              label="Security"
              value={9}
              {...a11yProps(9)}
            />
          )}
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <SchoolInfoTab />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ColorsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <AcademicYearTab />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <AvatarSettingsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <NavigationSettingsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <ExtraLessonSettingsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <LegalSettingsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <OnboardingSettingsTab />
      </TabPanel>

      {showEnquiries && (
        <TabPanel value={activeTab} index={8}>
          <EnquirySettingsTab />
        </TabPanel>
      )}

      {isOwner && (
        <TabPanel value={activeTab} index={9}>
          <SecurityTab />
        </TabPanel>
      )}
    </Box>
  );
}
